import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Link as LinkIcon, Zap, TrendingUp, Home, X, Menu } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_WORDMARK = 'https://customer-assets.emergentagent.com/job_aibl-orbit-fix/artifacts/x1t6q25d_Group%2083.png';

const navLinks = [
  { name: 'Home', href: '/', isPage: true, icon: Home },
  { name: 'Works With', href: '#works-with', isPage: false, icon: LinkIcon },
  { name: 'How It Works', href: '#how-it-works', isPage: false, icon: Terminal },
  { name: 'Plugins', href: '/plugins', isPage: true, icon: Zap },
  { name: 'enaibl', href: '/enaibl', isPage: true, isNew: true, icon: TrendingUp },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const pillRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for active section tracking
  useEffect(() => {
    if (location.pathname !== '/') return;
    
    const sections = ['developers', 'chains', 'works-with', 'how-it-works'];
    const observers = [];

    sections.forEach((sectionId) => {
      const section = document.getElementById(sectionId);
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(sectionId);
            }
          });
        },
        { threshold: 0.3, rootMargin: '-100px 0px -50% 0px' }
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, [location.pathname]);

  // Cursor glow effect on pill
  useEffect(() => {
    const pill = pillRef.current;
    if (!pill) return;

    const handleMouseMove = (e) => {
      const rect = pill.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      pill.style.background = `
        radial-gradient(
          120px circle at ${x}px ${y}px,
          rgba(255,255,255,0.06),
          transparent 60%
        ),
        rgba(255,255,255,0.03)
      `;
    };

    const handleMouseLeave = () => {
      pill.style.background = 'rgba(255,255,255,0.03)';
    };

    pill.addEventListener('mousemove', handleMouseMove);
    pill.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      pill.removeEventListener('mousemove', handleMouseMove);
      pill.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleNavClick = (link) => {
    setMobileMenuOpen(false);
    if (link.isPage) {
      navigate(link.href);
      // Scroll to top when navigating to a new page
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      // Anchor link - smooth scroll to section
      if (location.pathname !== '/') {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(link.href);
          if (el) {
            const navHeight = 80; // Account for navbar height
            const elementPosition = el.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: elementPosition - navHeight, behavior: 'smooth' });
          }
        }, 150);
      } else {
        const el = document.querySelector(link.href);
        if (el) {
          const navHeight = 80; // Account for navbar height
          const elementPosition = el.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: elementPosition - navHeight, behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isLinkActive = (link) => {
    if (link.isPage) {
      return location.pathname === link.href;
    }
    return activeSection === link.href.replace('#', '');
  };

  return (
    <>
      {/* CSS for animations */}
      <style>{`
        @keyframes nav-link-in {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes badge-pulse {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 0.5; }
        }
        
        .nav-link {
          opacity: 0;
          transform: translateY(-8px);
          animation: nav-link-in 0.4s ease forwards;
        }
        
        .nav-link:nth-child(1) { animation-delay: 0.1s; }
        .nav-link:nth-child(2) { animation-delay: 0.15s; }
        .nav-link:nth-child(3) { animation-delay: 0.2s; }
        .nav-link:nth-child(4) { animation-delay: 0.25s; }
        .nav-link:nth-child(5) { animation-delay: 0.3s; }
        
        .nav-link-text {
          position: relative;
          letter-spacing: 0em;
          transition: letter-spacing 0.25s ease;
        }
        
        .nav-link:hover .nav-link-text {
          letter-spacing: 0.02em;
        }
        
        .nav-link-text::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0%;
          height: 1px;
          background: rgba(255,255,255,0.3);
          transition: width 0.3s ease;
        }
        
        .nav-link:hover .nav-link-text::after {
          width: 100%;
        }
        
        .nav-new-badge {
          animation: badge-pulse 3s ease-in-out infinite;
        }
        
        .navbar-backdrop {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .navbar.scrolled .navbar-backdrop {
          opacity: 1;
        }
      `}</style>

      <nav
        data-testid="navbar"
        className={`navbar z-50 left-0 right-0 flex items-center justify-between mx-auto px-4 md:px-8 h-14 transition-all duration-300 ${
          scrolled ? 'scrolled md:top-3 md:fixed md:max-w-[1200px]' : 'md:top-4 md:fixed md:max-w-[1200px]'
        } relative md:fixed w-full`}
        style={{
          // On mobile: relative positioning (non-sticky), no gap at top
          // On desktop: fixed with top offset
          top: 0,
        }}
      >
        {/* Backdrop for scrolled state - only on desktop */}
        <div 
          className="navbar-backdrop absolute -inset-x-5 -inset-y-2 rounded-2xl pointer-events-none -z-10 hidden md:block"
          style={{
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
        />

        {/* Mobile navbar background */}
        <div 
          className="md:hidden absolute inset-0 -z-10"
          style={{
            background: 'rgba(0,0,0,0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        />

        {/* Logo - outside pill, left */}
        <a 
          href="/" 
          onClick={handleLogoClick}
          className="flex-shrink-0 z-10"
          data-testid="navbar-logo"
        >
          <img 
            src={LOGO_WORDMARK} 
            alt="aibl" 
            className="h-7 w-auto opacity-90 hover:opacity-100 transition-opacity duration-200" 
          />
        </a>

        {/* Glass Pill - center */}
        <div 
          ref={pillRef}
          className="hidden md:flex items-center gap-1 px-1.5 py-1 h-11 rounded-full transition-all duration-300"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            backdropFilter: 'blur(20px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.2)',
            boxShadow: `
              0 0 0 0.5px rgba(255,255,255,0.05) inset,
              0 2px 8px rgba(0,0,0,0.3),
              0 1px 2px rgba(0,0,0,0.2)
            `,
          }}
          data-testid="nav-pill"
        >
          {navLinks.map((link) => {
            const Icon = link.icon;
            const active = isLinkActive(link);
            
            return (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`nav-link flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap relative transition-all duration-250 ${
                  active 
                    ? 'text-white/90 bg-white/[0.06]' 
                    : link.isNew 
                      ? 'text-white/60 hover:text-white/95 hover:bg-white/[0.07]' 
                      : 'text-white/50 hover:text-white/95 hover:bg-white/[0.07]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                <Icon 
                  size={11} 
                  className={`transition-all duration-250 ${
                    active ? 'opacity-70' : 'opacity-40 group-hover:opacity-80'
                  }`}
                  style={{ opacity: active ? 0.7 : undefined }}
                />
                <span className="nav-link-text">{link.name}</span>
                
                {link.isNew && (
                  <span 
                    className="nav-new-badge text-[8px] font-bold uppercase tracking-[0.06em] text-black bg-white px-1.5 py-0.5 rounded ml-0.5"
                    style={{ lineHeight: 1.4 }}
                  >
                    NEW
                  </span>
                )}
                
                {/* Active indicator dot */}
                {active && (
                  <span 
                    className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white/50"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* CTA - outside pill, right */}
        <button 
          onClick={() => navigate('/login')}
          className="hidden md:flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[13px] font-semibold text-black bg-white transition-all duration-250 hover:bg-white/90 hover:-translate-y-0.5 flex-shrink-0"
          style={{
            boxShadow: '0 0 20px rgba(255,255,255,0.08)',
          }}
          data-testid="launch-app-btn"
        >
          Launch App
          <span className="text-sm transition-transform duration-200 group-hover:translate-x-0.5">→</span>
        </button>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6"
            style={{
              background: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(30px)',
              WebkitBackdropFilter: 'blur(30px)',
            }}
            data-testid="mobile-menu"
          >
            {/* Close button */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-5 right-5 p-3 text-white/70 hover:text-white transition-colors"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
            
            {/* Mobile nav links */}
            {navLinks.map((link, i) => {
              const Icon = link.icon;
              return (
                <motion.button
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => handleNavClick(link)}
                  className="flex items-center gap-3 text-xl font-medium text-white/70 hover:text-white transition-colors"
                >
                  <Icon size={18} className="opacity-50" />
                  {link.name}
                  {link.isNew && (
                    <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded bg-white text-black">
                      NEW
                    </span>
                  )}
                </motion.button>
              );
            })}
            
            {/* Mobile CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
              className="mt-6 bg-white text-black font-semibold px-8 py-3 rounded-full flex items-center gap-2"
              data-testid="mobile-launch-app-btn"
            >
              Launch App
              <span>→</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
